import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import {
  getReview, listDocuments, uploadDocuments, deleteDocument, updateDocument,
  startAnalysis, getDocumentTypes,
} from '../api/client';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png'];

const CLASSIFY_RULES = [
  // Multi-keyword
  [['wijziging', 'splitsing'],          'wijziging_splitsingsakte'],
  [['nen', '2580'],                      'nen2580_meetcertificaat'],
  [['gbo', 'inhoud'],                    'nen2580_meetcertificaat'],
  [['lijst', 'zaken'],                   'lijst_van_zaken'],
  [['aanslag', 'belasting'],             'aanslagbiljet'],
  [['kvk', 'kamer'],                     'kvk_uittreksel'],
  [['voorlopig', 'koop'],                'koopovereenkomst'],
  [['kadastrale', 'kaart'],              'kadastrale_kaart'],
  [['splitsing', 'tekening'],            'splitsingsakte'],
  [['energie', 'label'],                 'energielabel'],
  [['afschrift', 'erfpacht'],            'erfpachtakte'],
  [['akte', 'levering'],                 'leveringsakte'],
  [['informatie', 'eigendom'],           'eigendomsinformatie'],
  // Foundation & soil
  [['meetbouten'],                       'meetbouten'],
  [['bodeminformatie'],                  'bodeminformatie'],
  [['bodemrapport'],                     'bodeminformatie'],
  [['omgevingsdienst'],                  'bodeminformatie'],
  // Tax & valuation
  [['nen2580'],                          'nen2580_meetcertificaat'],
  [['meetcertificaat'],                  'nen2580_meetcertificaat'],
  [['meetrapport'],                      'nen2580_meetcertificaat'],
  [['aanslagbiljet'],                    'aanslagbiljet'],
  [['aanslag'],                          'aanslagbiljet'],
  [['belastingen'],                      'aanslagbiljet'],
  [['woz'],                              'woz_beschikking'],
  // Ownership
  [['bestemmingsplan'],                  'bestemmingsplan'],
  [['bag'],                              'bag_extract'],
  [['eigendomsinformatie'],              'eigendomsinformatie'],
  [['leveringsakte'],                    'leveringsakte'],
  [['levering'],                         'leveringsakte'],
  [['eigendomsakte'],                    'eigendomsakte'],
  [['eigendom'],                         'eigendomsakte'],
  [['openbaar', 'register'],             'eigendomsinformatie'],
  [['hyp4'],                             'eigendomsinformatie'],
  // VvE
  [['huishoudelijk'],                    'huishoudelijk_reglement'],
  [['hhr'],                              'huishoudelijk_reglement'],
  [['servicekosten'],                    'servicekosten_overzicht'],
  [['notulen'],                          'vve_notulen'],
  [['vergadering'],                      'vve_notulen'],
  [['alv'],                              'vve_notulen'],
  [['jaarrekening'],                     'vve_jaarrekening'],
  [['jaarverslag'],                      'vve_jaarrekening'],
  [['begroting'],                        'vve_begroting'],
  [['opstalverzekering'],                'vve_opstalverzekering'],
  [['verzekering'],                      'vve_opstalverzekering'],
  [['polis'],                            'vve_opstalverzekering'],
  [['splitsingsakte'],                   'splitsingsakte'],
  [['splitsing'],                        'splitsingsakte'],
  // Maintenance
  [['dmjop'],                            'bouwkundig_rapport'],
  [['conditiemeting'],                   'bouwkundig_rapport'],
  [['bouwkundig'],                       'bouwkundig_rapport'],
  [['bouwtechnisch'],                    'bouwkundig_rapport'],
  [['inspectierapport'],                 'bouwkundig_rapport'],
  [['inspectie'],                        'bouwkundig_rapport'],
  [['mjop'],                             'mjop'],
  [['meerjarenonderhoud'],               'mjop'],
  [['onderhoudsplan'],                   'mjop'],
  // Building permits & renovation
  [['omgevingsvergunning'],              'omgevingsvergunning'],
  [['omgevingsloket'],                   'omgevingsvergunning'],
  [['bouwvergunning'],                   'omgevingsvergunning'],
  [['bouwtekening'],                     'omgevingsvergunning'],
  [['besluit', 'verbouwing'],            'omgevingsvergunning'],
  [['verbouwingsbesluit'],               'omgevingsvergunning'],
  [['verbouwing'],                       'omgevingsvergunning'],
  [['vergunning'],                       'omgevingsvergunning'],
  // Ground lease
  [['erfpacht'],                         'erfpachtakte'],
  [['grondhuur'],                        'erfpachtakte'],
  // Purchase
  [['nvm'],                              'nvm_vragenlijst'],
  [['vragenlijst'],                      'nvm_vragenlijst'],
  [['koopovereenkomst'],                 'koopovereenkomst'],
  [['koopcontract'],                     'koopovereenkomst'],
  [['brochure'],                         'brochure'],
  // Energy
  [['energielabel'],                     'energielabel'],
  [['label'],                            'energielabel'],
  // Cadastral fallback
  [['kadaster'],                         'kadastrale_informatie'],
  [['kadastrale'],                       'kadastrale_informatie'],
  // Floor plans
  [['plattegrond'],                      'plattegrond'],
  [['floorplan'],                        'plattegrond'],
  [['design'],                           'plattegrond'],
  [['tekening'],                         'plattegrond'],
  [['verdieping'],                       'plattegrond'],
  // Corporate
  [['ubo'],                              'ubo_register'],
  [['kvk'],                              'kvk_uittreksel'],
  [['uittreksel'],                       'kvk_uittreksel'],
];

function classifyFilename(filename) {
  const name = filename.toLowerCase().replace(/[_\-\.,]/g, ' ');
  for (const [keywords, docType] of CLASSIFY_RULES) {
    if (keywords.every(kw => name.includes(kw))) return docType;
  }
  const dotIdx = filename.lastIndexOf('.');
  const ext = dotIdx >= 0 ? filename.slice(dotIdx + 1).toLowerCase() : '';
  if (IMAGE_EXTS.includes(ext)) return 'plattegrond';
  return 'overige';
}

// Client-side mirror of the backend auto-naming (app.py generate_display_name) so the
// pending list previews the system-assigned name before upload.
const GENERIC_NAME_TOKENS = new Set([
  'scan', 'scanned', 'document', 'doc', 'untitled', 'image', 'img', 'photo', 'picture',
  'pic', 'file', 'new', 'copy', 'download', 'attachment', 'page', 'dsc', 'afbeelding',
  'naamloos', 'bestand', 'pagina',
]);

function isDescriptiveFilename(stem) {
  const s = (stem || '').trim();
  if (!s) return false;
  const norm = s.replace(/[_\-.,()]+/g, ' ').trim().toLowerCase();
  if (!norm) return false;
  if (norm.replace(/[^a-z]/g, '').length < 4) return false;
  if (/^(img|dsc|dscn|p|scan|photo|pic|image)\s*\d+$/.test(norm)) return false;
  const meaningful = norm.split(/\s+/).filter(t => /^[a-z]{3,}$/.test(t) && !GENERIC_NAME_TOKENS.has(t));
  return meaningful.length > 0;
}

function previewSystemName(filename, docTypeLabel) {
  const dot = filename.lastIndexOf('.');
  const stem = dot >= 0 ? filename.slice(0, dot) : filename;
  if (isDescriptiveFilename(stem)) return null;
  return docTypeLabel || 'Unnamed document';
}

const TYPE_LABEL = {
  standard_apartment: 'Apartment (VvE)',
  house:              'House',
  erfpacht_apartment: 'Erfpacht Apt.',
  erfpacht_house:     'Erfpacht House',
};

const prefersReduced = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function ReviewView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInput = useRef();
  const root = useRef(null);

  const [review, setReview]           = useState(null);
  const [documents, setDocuments]     = useState([]);
  const [docTypes, setDocTypes]       = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [starting, setStarting]       = useState(false);
  const [dragOver, setDragOver]       = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    getReview(id).then(setReview);
    listDocuments(id).then(setDocuments);
    getDocumentTypes().then(setDocTypes);
  }, [id]);

  /* Editorial header reveal once the review has loaded */
  useLayoutEffect(() => {
    if (!review || prefersReduced) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.line-mask > span',
        { yPercent: 112 },
        { yPercent: 0, duration: 0.85, ease: 'power3.out', stagger: 0.1, delay: 0.05 },
      );
    }, root);
    return () => ctx.revert();
  }, [review]);

  function addFiles(files) {
    const newPending = Array.from(files).map(f => ({
      file: f,
      doc_type: classifyFilename(f.name),
      isZip: f.name.toLowerCase().endsWith('.zip'),
    }));
    setPendingFiles(p => [...p, ...newPending]);
  }

  function onFilesSelected(e) { addFiles(e.target.files); e.target.value = ''; }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  function updatePendingType(idx, doc_type) {
    setPendingFiles(p => p.map((pf, i) => i === idx ? { ...pf, doc_type } : pf));
  }

  function removePending(idx) { setPendingFiles(p => p.filter((_, i) => i !== idx)); }

  async function handleUpload() {
    if (!pendingFiles.length) return;
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      pendingFiles.forEach(pf => {
        formData.append('files', pf.file);
        formData.append('doc_types', pf.doc_type);
      });
      await uploadDocuments(id, formData);
      setPendingFiles([]);
      setDocuments(await listDocuments(id));
    } catch {
      setError('Upload failed. Accepted formats: PDF, DOCX, or ZIP containing PDFs.');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(docId) {
    await deleteDocument(id, docId);
    setDocuments(d => d.filter(doc => doc.id !== docId));
  }

  async function handleStart() {
    setStarting(true); setError('');
    try {
      const { run_id } = await startAnalysis(id);
      navigate(`/reviews/${id}/progress/${run_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start analysis.');
      setStarting(false);
    }
  }

  const docTypesByCategory = docTypes.reduce((acc, dt) => {
    const cat = dt.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(dt);
    return acc;
  }, {});

  const canStart = documents.length > 0 && !starting;
  if (!review) return <div className="page"><p className="text-muted">Loading...</p></div>;

  return (
    <div className="page theme-cream" ref={root}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 44 }}>
        <div style={{ marginBottom: 18 }}>
          <Link to="/" style={{ fontSize: 13, color: 'var(--text-label)' }}>← Dashboard</Link>
        </div>
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          Package #{String(id).padStart(2, '0')} — Upload &amp; Prepare
        </div>
        <div className="page-header-row" style={{ alignItems: 'flex-end' }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="display-lg" style={{ marginBottom: 12 }}>
              <span className="line-mask"><span>{review.property_address}</span></span>
            </h1>
            <p className="text-muted">
              {TYPE_LABEL[review.property_type] || review.property_type}
              {' · '}
              {review.seller_type === 'corporate' ? 'Corporate seller' : 'Private seller'}
              {review.agent_name && ` · ${review.agent_name}`}
            </p>
          </div>
          {review.status === 'complete' && (
            <Link to={`/reviews/${id}/results`}>
              <button className="btn btn-primary btn-dot">View Results →</button>
            </Link>
          )}
        </div>
      </div>

      {/* Documents card */}
      <motion.div
        className="card"
        style={{ marginBottom: 16 }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
      >
        <div className="flex-between" style={{ marginBottom: 20 }}>
          <div className="sec-head" style={{ marginBottom: 0 }}>
            <span className="idx">01</span>
            <h2>Documents</h2>
          </div>
          <span className="badge badge-gray">{documents.length} uploaded</span>
        </div>

        {/* Uploaded docs table */}
        {documents.length > 0 && (
          <div className="table-wrapper" style={{ marginBottom: 24 }}>
            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id}>
                    <td style={{ maxWidth: 240 }}>
                      <div style={{ fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.display_name || doc.filename}
                      </div>
                      {doc.display_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span className="badge badge-amber" style={{ fontSize: 10, padding: '1px 6px' }}>system-named</span>
                          <span style={{ fontSize: 11, color: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {doc.filename}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-control"
                        style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, width: 180 }}
                        value={doc.doc_type}
                        onChange={async e => {
                          const updated = await updateDocument(id, doc.id, { doc_type: e.target.value });
                          setDocuments(d => d.map(x => x.id === doc.id ? { ...x, doc_type: updated.doc_type } : x));
                        }}
                      >
                        {Object.entries(docTypesByCategory).map(([cat, types]) => (
                          <optgroup key={cat} label={cat}>
                            {types.map(dt => (
                              <option key={dt.id} value={dt.id}>{dt.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td className="text-muted text-sm">{doc.file_size_kb?.toFixed(0)} KB</td>
                    <td>
                      {doc.parse_status === 'scanned'  && <span className="badge badge-amber">Scanned</span>}
                      {doc.parse_status === 'success'  && <span className="badge badge-green">✓ OK</span>}
                      {doc.parse_status === 'pending'  && <span className="badge badge-gray">Pending</span>}
                      {doc.parse_status === 'failed'   && <span className="badge badge-red">Error</span>}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDelete(doc.id)}
                        style={{ color: 'var(--red)', padding: '4px 10px', borderRadius: 8 }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Drop zone */}
        <div
          className={`upload-area${dragOver ? ' drag-over' : ''}`}
          onClick={() => fileInput.current.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <div className="drop-orb">↑</div>
          <p style={{ fontWeight: 500, color: 'var(--text)', fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
            Drop your seller package here
          </p>
          <p style={{ color: 'var(--text-label)', fontSize: 13, marginTop: 6 }}>
            PDF, DOCX or ZIP — Dutch language supported
          </p>
          <p style={{ color: 'var(--ink)', fontSize: 13, marginTop: 10, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            or click to browse
          </p>
          <input
            ref={fileInput} type="file" multiple
            accept=".pdf,.docx,.doc,.zip,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={onFilesSelected}
          />
        </div>

        {/* Pending files */}
        <AnimatePresence>
          {pendingFiles.length > 0 && (
            <motion.div
              style={{ marginTop: 20 }}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-label)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                {pendingFiles.every(pf => pf.isZip) ? 'Ready to upload' : 'Label each file before uploading'}
              </p>
              {pendingFiles.map((pf, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ delay: idx * 0.04 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 0', borderBottom: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontSize: 18 }}>{pf.isZip ? '📦' : '📄'}</span>
                  {(() => {
                    const lbl = pf.doc_type && pf.doc_type !== 'overige'
                      ? (docTypes.find(d => d.id === pf.doc_type)?.label) : null;
                    const sysName = pf.isZip ? null : previewSystemName(pf.file.name, lbl);
                    return (
                      <span style={{ flex: 1, overflow: 'hidden' }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sysName || pf.file.name}
                        </span>
                        {sysName && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <span className="badge badge-amber" style={{ fontSize: 10, padding: '1px 6px' }}>system-named</span>
                            <span style={{ fontSize: 11, color: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pf.file.name}</span>
                          </span>
                        )}
                      </span>
                    );
                  })()}
                  {pf.isZip ? (
                    <span style={{ fontSize: 12, color: 'var(--text-label)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                      Will be extracted &amp; auto-labelled
                    </span>
                  ) : (
                    <select
                      className="form-control"
                      style={{ width: 260, fontSize: 13, borderRadius: 8 }}
                      value={pf.doc_type}
                      onChange={e => updatePendingType(idx, e.target.value)}
                    >
                      {Object.entries(docTypesByCategory).map(([cat, types]) => (
                        <optgroup key={cat} label={cat}>
                          {types.map(dt => (
                            <option key={dt.id} value={dt.id}>{dt.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => removePending(idx)}
                    style={{ padding: '4px 10px', borderRadius: 8, color: 'var(--red)' }}
                  >
                    ✕
                  </button>
                </motion.div>
              ))}
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                  {uploading ? 'Uploading...' : pendingFiles.length === 1 && pendingFiles[0].isZip
                    ? 'Upload package'
                    : `Upload ${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}`}
                </button>
                <button className="btn btn-secondary" onClick={() => setPendingFiles([])}>Cancel</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Run analysis */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.35 }}
      >
        <div className="sec-head" style={{ marginBottom: 8 }}>
          <span className="idx">02</span>
          <h2>Run Analysis</h2>
        </div>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
          Start the AI analysis once all documents are uploaded. The pipeline runs completeness check,
          AI document analysis, rules engine (DRE), and cross-document synthesis.
        </p>

        {error && (
          <div style={{
            background: 'rgba(231,76,60,0.06)', color: 'var(--red)',
            border: '1px solid rgba(231,76,60,0.2)', borderRadius: 10,
            padding: '12px 16px', marginBottom: 16, fontSize: 14,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleStart}
            disabled={!canStart}
            style={{ fontSize: 15, padding: '14px 32px' }}
          >
            {starting ? 'Starting...' : '▶ Run Analysis'}
          </button>
          {documents.length === 0 && (
            <span className="text-sm text-muted">Upload documents first.</span>
          )}
        </div>
      </motion.div>
    </div>
  );
}
